import log from '@/utils/useLogger'

/**
 * Browser-based notification helper.
 *
 * Surfaces OS-level notifications via the Web Notifications API so that events
 * (e.g. a device-group takeover request) reach the user even when they are not
 * looking at the app — on another browser tab, another window, or with the app
 * minimised. See FLY3D-866.
 */

export interface BrowserNotificationOptions {
  /** Notification heading. */
  title: string
  /** Body text shown under the title. */
  body?: string
  /**
   * Grouping tag. Notifications sharing a tag replace one another instead of
   * stacking, so repeated requests don't spam the user.
   */
  tag?: string
  /** Auto-dismiss after this many ms (0 keeps it until the user dismisses). */
  autoCloseMs?: number
  /** Invoked when the user clicks the notification (after the app is focused). */
  onClick?: () => void
}

const isSupported = () => typeof window !== 'undefined' && 'Notification' in window

/**
 * Whether the app is currently out of the user's view — another tab is active,
 * the window is blurred, or it is minimised. This is the condition under which
 * an in-app modal alone would go unseen.
 */
const isAppUnfocused = () =>
  typeof document === 'undefined' || document.hidden || !document.hasFocus()

export function useBrowserNotification() {
  /**
   * Ask the user for notification permission if we don't have an answer yet.
   * Safe to call repeatedly — it no-ops once a decision (granted/denied) exists.
   * Must be triggered from a user gesture or app init for the prompt to show.
   */
  const requestPermission = async (): Promise<NotificationPermission> => {
    if (!isSupported()) return 'denied'
    if (Notification.permission !== 'default') return Notification.permission

    try {
      const permission = await Notification.requestPermission()
      log.info(`Browser notification permission: ${permission}`)
      return permission
    } catch (error) {
      log.error('Failed to request notification permission:', error)
      return 'denied'
    }
  }

  /**
   * Show a browser notification. By default it only fires while the app is
   * unfocused, since an in-app modal already covers the focused case.
   * Pass `force` to always show.
   */
  const notify = (
    options: BrowserNotificationOptions,
    { force = false }: { force?: boolean } = {},
  ): Notification | null => {
    if (!isSupported()) {
      log.warn('Browser notification skipped: Notification API not supported')
      return null
    }
    if (Notification.permission !== 'granted') {
      log.warn(`Browser notification skipped: permission is "${Notification.permission}"`)
      return null
    }
    if (!force && !isAppUnfocused()) {
      log.info('Browser notification skipped: app is focused (in-app modal is visible)')
      return null
    }

    log.info('Showing browser notification:', options.title)
    try {
      const notification = new Notification(options.title, {
        body: options.body,
        tag: options.tag,
        // Keep the notification on screen until acknowledged where the OS supports it.
        requireInteraction: true,
      })

      notification.onclick = () => {
        try {
          window.focus()
          options.onClick?.()
        } catch (error) {
          log.error('Error handling notification click:', error)
        } finally {
          notification.close()
        }
      }

      if (options.autoCloseMs && options.autoCloseMs > 0) {
        setTimeout(() => notification.close(), options.autoCloseMs)
      }

      return notification
    } catch (error) {
      log.error('Failed to show browser notification:', error)
      return null
    }
  }

  return {
    isSupported,
    isAppUnfocused,
    requestPermission,
    notify,
  }
}
